import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import { Shapefile } from '../../app/feathers/districts/Shapefile';
import {
	buildTransformPreviewSamples,
	ColumnTransformDefinition,
	createUniqueColumnName,
	Transform,
} from './columnTransforms';

interface TransformColumnModalProps {
	open: boolean;
	sourceColumn: string | null;
	existingHeaders: string[];
	sampleRows: Shapefile[];
	sampleIndexes: number[];
	onClose: () => void;
	onApply: (definition: ColumnTransformDefinition) => void;
}

const transformTypeOptions: Array<{ value: Transform['type']; label: string }> = [
	{ value: 'trim', label: 'Trim' },
	{ value: 'lowercase', label: 'Lowercase' },
	{ value: 'uppercase', label: 'Uppercase' },
	{ value: 'pretty_case', label: 'Pretty case' },
	{ value: 'remove_spaces', label: 'Remove spaces' },
	{ value: 'remove_punctuation', label: 'Remove punctuation' },
	{ value: 'to_number', label: 'To number' },
	{ value: 'replace', label: 'Replace' },
	{ value: 'regex_replace', label: 'Regex replace' },
	{ value: 'extract_regex', label: 'Extract regex' },
	{ value: 'split', label: 'Split' },
	{ value: 'strip_prefix', label: 'Strip prefix' },
	{ value: 'strip_suffix', label: 'Strip suffix' },
];

const createTransformByType = (type: Transform['type']): Transform => {
	switch (type) {
		case 'replace':
			return { type: 'replace', find: '', replacement: '', caseSensitive: true };
		case 'regex_replace':
			return { type: 'regex_replace', pattern: '', replacement: '', flags: 'g' };
		case 'extract_regex':
			return { type: 'extract_regex', pattern: '', flags: '' };
		case 'split':
			return { type: 'split', delimiter: ' ', take: 'first' };
		case 'strip_prefix':
			return { type: 'strip_prefix', value: '', caseSensitive: true };
		case 'strip_suffix':
			return { type: 'strip_suffix', value: '', caseSensitive: true };
		default:
			return { type };
	}
};

export const TransformColumnModal = ({
	open,
	sourceColumn,
	existingHeaders,
	sampleRows,
	sampleIndexes,
	onClose,
	onApply,
}: TransformColumnModalProps) => {
	const [targetColumn, setTargetColumn] = useState('');
	const [transforms, setTransforms] = useState<Transform[]>([]);

	useEffect(() => {
		if (!open || !sourceColumn) return;
		setTransforms([]);
		setTargetColumn(
			createUniqueColumnName(`${sourceColumn}_transformed`, existingHeaders)
		);
	}, [open, sourceColumn, existingHeaders]);

	const targetColumnTaken = useMemo(() => {
		return existingHeaders.includes(targetColumn.trim());
	}, [existingHeaders, targetColumn]);

	const previewSamples = useMemo(() => {
		if (!sourceColumn) return [];
		return buildTransformPreviewSamples(sampleRows, sourceColumn, transforms, sampleIndexes);
	}, [sampleRows, sourceColumn, transforms, sampleIndexes]);

	const updateTransform = (index: number, nextTransform: Transform) => {
		setTransforms((current) => {
			const next = [...current];
			next[index] = nextTransform;
			return next;
		});
	};

	const addTransform = () => {
		setTransforms((current) => [...current, createTransformByType('trim')]);
	};

	const removeTransform = (index: number) => {
		setTransforms((current) => current.filter((_, transformIndex) => transformIndex !== index));
	};

	const moveTransform = (index: number, direction: -1 | 1) => {
		setTransforms((current) => {
			const nextIndex = index + direction;
			if (nextIndex < 0 || nextIndex >= current.length) return current;
			const next = [...current];
			const temp = next[index];
			next[index] = next[nextIndex];
			next[nextIndex] = temp;
			return next;
		});
	};

	const canApply = Boolean(
		sourceColumn &&
		targetColumn.trim() &&
		transforms.length > 0 &&
		!targetColumnTaken
	);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
			<DialogTitle>Transform column</DialogTitle>
			<DialogContent dividers>
				<Typography variant='body2' style={{ marginBottom: 12 }}>
					Source column: <b>{sourceColumn || '-'}</b>
				</Typography>
				<TextField
					fullWidth
					size='small'
					label='New column name'
					value={targetColumn}
					onChange={(event) => setTargetColumn(event.target.value)}
					error={targetColumnTaken}
					helperText={targetColumnTaken ? 'Column name already exists.' : ''}
					style={{ marginBottom: 12 }}
				/>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 10,
						backgroundColor: '#fafafa',
						border: '1px solid #bdbdbd',
						borderRadius: 10,
						padding: 12,
					}}
				>
					{transforms.length === 0 && (
						<Typography variant='body2' color='textSecondary'>
							Add one or more transforms to build a chain.
						</Typography>
					)}
					{transforms.map((transform, index) => (
						<Paper key={`${transform.type}-${index}`} style={{ padding: 10 }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<Select
									size='small'
									value={transform.type}
									onChange={(event) => {
										updateTransform(index, createTransformByType(event.target.value as Transform['type']));
									}}
								>
									{transformTypeOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
								<Typography variant='body2'>Step {index + 1}</Typography>
								<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
									<IconButton
										size='small'
										onClick={() => moveTransform(index, -1)}
										disabled={index === 0}
									>
										<ArrowUpward fontSize='small' />
									</IconButton>
									<IconButton
										size='small'
										onClick={() => moveTransform(index, 1)}
										disabled={index === transforms.length - 1}
									>
										<ArrowDownward fontSize='small' />
									</IconButton>
									<IconButton size='small' onClick={() => removeTransform(index)}>
										<Delete fontSize='small' />
									</IconButton>
								</div>
							</div>
							<TransformInputs transform={transform} onChange={(next) => updateTransform(index, next)} />
						</Paper>
					))}
					<div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
						<Button variant='outlined' onClick={addTransform}>
							Add transform
						</Button>
					</div>
				</div>
				<Typography variant='subtitle2' style={{ margin: '16px 0 8px' }}>
					Sample preview
				</Typography>
				<TableContainer component={Paper} style={{ maxHeight: 260 }}>
					<Table size='small' stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Original value</TableCell>
								<TableCell>Final value</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{previewSamples.map((sample) => (
								<TableRow key={sample.rowIndex}>
									<TableCell>{sample.originalValue || '(blank)'}</TableCell>
									<TableCell>{sample.finalValue || '(blank)'}</TableCell>
								</TableRow>
							))}
							{previewSamples.length === 0 && (
								<TableRow>
									<TableCell colSpan={2}>No sampled rows available.</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={() => {
						if (!sourceColumn) return;
						onApply({
							source: sourceColumn,
							target: targetColumn.trim(),
							transforms,
						});
					}}
					disabled={!canApply}
				>
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const TransformInputs = ({
	transform,
	onChange,
}: {
	transform: Transform;
	onChange: (nextTransform: Transform) => void;
}) => {
	if (transform.type === 'replace') {
		return (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginTop: 10 }}>
				<TextField
					size='small'
					label='Find'
					value={transform.find}
					onChange={(event) => onChange({ ...transform, find: event.target.value })}
				/>
				<TextField
					size='small'
					label='Replacement'
					value={transform.replacement}
					onChange={(event) => onChange({ ...transform, replacement: event.target.value })}
				/>
				<Select
					size='small'
					value={transform.caseSensitive !== false ? 'true' : 'false'}
					onChange={(event) => onChange({ ...transform, caseSensitive: event.target.value === 'true' })}
				>
					<MenuItem value='true'>Case sensitive</MenuItem>
					<MenuItem value='false'>Ignore case</MenuItem>
				</Select>
			</div>
		);
	}
	if (transform.type === 'regex_replace') {
		return (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 8, marginTop: 10 }}>
				<TextField
					size='small'
					label='Pattern'
					value={transform.pattern}
					onChange={(event) => onChange({ ...transform, pattern: event.target.value })}
				/>
				<TextField
					size='small'
					label='Replacement'
					value={transform.replacement}
					onChange={(event) => onChange({ ...transform, replacement: event.target.value })}
				/>
				<TextField
					size='small'
					label='Flags'
					value={transform.flags || ''}
					onChange={(event) => onChange({ ...transform, flags: event.target.value })}
				/>
			</div>
		);
	}
	if (transform.type === 'extract_regex') {
		return (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, marginTop: 10 }}>
				<TextField
					size='small'
					label='Pattern'
					value={transform.pattern}
					onChange={(event) => onChange({ ...transform, pattern: event.target.value })}
				/>
				<TextField
					size='small'
					label='Flags'
					value={transform.flags || ''}
					onChange={(event) => onChange({ ...transform, flags: event.target.value })}
				/>
			</div>
		);
	}
	if (transform.type === 'split') {
		return (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8, marginTop: 10 }}>
				<TextField
					size='small'
					label='Delimiter'
					value={transform.delimiter}
					onChange={(event) => onChange({ ...transform, delimiter: event.target.value })}
				/>
				<Select
					size='small'
					value={typeof transform.take === 'number' ? String(transform.take) : transform.take}
					onChange={(event) => {
						const nextValue = event.target.value;
						if (nextValue === 'first' || nextValue === 'last') {
							onChange({ ...transform, take: nextValue });
							return;
						}
						const parsed = parseInt(nextValue, 10);
						onChange({ ...transform, take: Number.isNaN(parsed) ? 0 : parsed });
					}}
				>
					<MenuItem value='first'>Take first</MenuItem>
					<MenuItem value='last'>Take last</MenuItem>
					<MenuItem value='0'>Take index 0</MenuItem>
					<MenuItem value='1'>Take index 1</MenuItem>
					<MenuItem value='2'>Take index 2</MenuItem>
				</Select>
			</div>
		);
	}
	if (transform.type === 'strip_prefix' || transform.type === 'strip_suffix') {
		return (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginTop: 10 }}>
				<TextField
					size='small'
					label={transform.type === 'strip_prefix' ? 'Prefix' : 'Suffix'}
					value={transform.value}
					onChange={(event) => onChange({ ...transform, value: event.target.value })}
				/>
				<Select
					size='small'
					value={transform.caseSensitive !== false ? 'true' : 'false'}
					onChange={(event) => onChange({ ...transform, caseSensitive: event.target.value === 'true' })}
				>
					<MenuItem value='true'>Case sensitive</MenuItem>
					<MenuItem value='false'>Ignore case</MenuItem>
				</Select>
			</div>
		);
	}
	return null;
};
